import { postFeedbackRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useState } from "react"
import type { SubmitHandler, UseFormReturn } from "react-hook-form"
import { useForm, useWatch } from "react-hook-form"
import type { FeedbackFormValues } from "src/components"
import { trackEvent } from "src/components/adobeAnalytics"
import { useBff } from "src/hooks"

export const useFeedback = (): {
	form: UseFormReturn<FeedbackFormValues>
	isSubmitted: boolean
	onReset: () => void
	onSubmit: SubmitHandler<FeedbackFormValues>
} => {
	const [isSubmitted, setIsSubmitted] = useState(false)

	const form = useForm<FeedbackFormValues>({
		defaultValues: { roles: [], text: "" },
	})

	const values = useWatch({ control: form.control })

	const postFeedback = useBff(
		postFeedbackRoute,
		{
			body: {
				rating: values.rating!,
				roles: values.roles && values.roles.length > 0 ? values.roles : undefined,
				text: values.text?.trim() || undefined,
				url: window.location.href,
			},
			pathParams: {},
			queryParams: {},
		},
		"inline",
	)

	const trackAnalyticsEvent = useCallback(
		() =>
			trackEvent({
				action: "feedback_rating",
				category: "dealer_feedback",
				name: values.roles?.join(", ") || undefined,
				value: values.rating,
			}),
		[values.rating, values.roles],
	)

	const onSubmit: SubmitHandler<FeedbackFormValues> = useCallback(async () => {
		trackAnalyticsEvent()
		try {
			await postFeedback()
			setIsSubmitted(true)
		} catch {
			// error is handled by useBff's error notification
		}
	}, [trackAnalyticsEvent, postFeedback])

	const onReset = useCallback(() => {
		setIsSubmitted(false)
		form.reset()
	}, [form])

	return {
		form,
		isSubmitted,
		onReset,
		onSubmit,
	}
}
